import { Link } from 'react-router';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PROTOCOL_WORKFLOW_TYPE } from '@/modules/protocols/constants';
import type { WorkflowSummary } from '@/types';

interface UsedInProtocolsSidebarProps {
	protocols: WorkflowSummary[];
	isLoading: boolean;
	onClose: () => void;
}

/** The protocols that use an action, laid out like the call details panel. */
export const UsedInProtocolsSidebar = ({ protocols, isLoading, onClose }: UsedInProtocolsSidebarProps) => (
	<aside className='flex h-full w-[340px] shrink-0 flex-col border-l border-gray-500 bg-white'>
		<div className='flex h-[60px] shrink-0 items-center justify-between px-4 py-3'>
			<p className='text-[13px] font-medium leading-[18px] text-text-body'>Used in protocols</p>
			<Button variant='ghost' size='icon-sm' onClick={onClose} className='text-gray-900' aria-label='Close'>
				<X className='size-[14px]' />
			</Button>
		</div>
		<div className='h-px w-full shrink-0 bg-gray-500' />
		<div className='relative flex-1 overflow-y-auto [scrollbar-gutter:stable]'>
			<div className='flex flex-col gap-1 p-2'>
				{isLoading ? (
					<Skeleton className='h-9 rounded-lg' />
				) : !protocols.length ? (
					<p className='p-2 text-[12px] text-muted-foreground'>Not used in any protocol yet.</p>
				) : (
					protocols.map((protocol) => (
						<Link
							key={protocol.id}
							to={
								protocol.type === PROTOCOL_WORKFLOW_TYPE
									? `/training/protocols/${protocol.id}`
									: `/training/workflows/v2/${protocol.id}`
							}
							className='flex items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-gray-50 transition-colors'
						>
							<span className='truncate text-[13px] text-ink'>{protocol.name}</span>
							<Badge variant={protocol.status ? 'success' : 'secondary'}>
								{protocol.status ? 'Active' : 'Inactive'}
							</Badge>
						</Link>
					))
				)}
			</div>
		</div>
	</aside>
);
